import { CSSResultGroup, html, LitElement } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { style } from './toast.style.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { fontStyles } from 'elements/_shared/fonts.style.js'


initializeIconsByRole({
  'cse-toast.error': { name: 'octagon-xmark', style: 'solid' },
  'cse-toast.info': { name: 'circle-info', style: 'solid' },
  'cse-toast.success': { name: 'circle-check', style: 'solid' },
  'cse-toast.warning': { name: 'triangle-exclamation', style: 'solid' },
})

export const toastVariants = ['error', 'info', 'success', 'warning'] as const
export type toastVariantTypes = typeof toastVariants

@customElement('cse-toast')
export class CSEToast extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, fontStyles, style]

  @property({ type: String })
  variant: toastVariantTypes[number] = 'info'

  @property({ type: Boolean, attribute: 'show-close-button' })
  showCloseButton = true

  @property({ type: String })
  message = ''

  private renderIcon = (variantName: string) => html`
    <cse-icon
      icon-role="cse-toast.${variantName}"
      class="icon"
      size="lg"
    ></cse-icon>
  `

  private handleCloseClick = () => {
    this.dispatchEvent(new CustomEvent('hide-toast', {
      bubbles: true,
      composed: true
    }))
  }

  private handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      this.handleCloseClick()
    }
  }

  override render() {
    return html`
      <div 
        class="cse-toast variant-${this.variant}"
        tabindex="0"
        role="alert"
        aria-live="polite"
        aria-atomic="true"
        aria-label="${t('toast_notification', { ns: 'cse-toast', default: '{{variable_message}} notification', variable_message: this.message || this.variant })}"
        @keydown=${this.handleKeyDown}
      >
        ${this.renderIcon(this.variant)}
        <div class="content">
          <slot class="title font-body"></slot>
          <slot name="action"></slot>
        </div>
        ${this.showCloseButton ? html`
          <cse-icon-button
            class="close-button"
            variant="secondary"
            size="small"
            aria-label="${t('dismiss_notification', { ns: 'cse-toast', default: 'Dismiss notification' })}"
            @click=${this.handleCloseClick}
          >
            <cse-icon icon-role="cse-icon-button.clear"></cse-icon>
        </cse-icon-button>
        ` : ''}
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cse-toast': CSEToast
  }
}
